<template>
  <div id="events">
    <div class="row" v-if="loading">
      <div class="col-12 text-center my-4">
        <font-awesome-icon icon="running" class="fa-3x running-animate" />
        <div class="mt-2">{{$t('events.loading') || 'Loading events...'}}</div>
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-sm-6 col-lg-6 mb-4 d-flex align-items-stretch" v-for="(event, idx) of events" v-bind:key="idx">
        <router-link class="event-link flex-fill" :to="{name: 'eventDetail', params:{slug: event.slug}}">
          <div class="event-card card h-100 border-0 shadow-sm">
            <div class="event-img-wrapper position-relative">
              <img class="card-img-top" :alt="event.name" v-bind:src="event.image" data-holder-rendered="true">
              <span v-if="isUpcomingEvent(event.from*1000)" class="event-badge badge-upcoming position-absolute">{{ $t('events.upcoming') }}</span>
              <span v-if="isExpiredEvent(event.to*1000)" class="event-badge badge-expired position-absolute">{{ $t('events.expired') }}</span>
            </div>
            <div class="card-body py-2 px-2">
              <div class="card-text text-center mb-1">
                <span class="event-type-icon mr-1"><font-awesome-icon icon="swimmer" v-if="event.type==='Swim'"/></span>
                <span class="event-type-icon mr-1"><font-awesome-icon icon="biking" v-if="event.type==='Ride'"/></span>
                <span class="event-type-icon mr-1"><font-awesome-icon icon="running" v-if="event.type==='Run'"/></span>
                <span v-for="(type, idx) in event.type" :key="idx" class="event-type-icon mr-1">
                  <font-awesome-icon icon="swimmer" v-if="type=='Swim'"/>
                  <font-awesome-icon icon="biking" v-if="type=='Ride'"/>
                  <font-awesome-icon icon="running" v-if="type=='Run'"/>
                  <font-awesome-icon icon="walking" v-if="type=='Walk'"/>
                </span>
                <span class="event-title text-capitalize ml-1">{{event.name}}</span>
              </div>
              <div class="event-date text-center text-muted small">
                {{event.from * 1000 | formatTimestamp }} - {{event.to * 1000 | formatTimestamp}}
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>

    <div class="row" v-if="hasMore && !loading && events.length">
      <div class="col-12 text-center my-3">
        <button class="btn btn-outline-primary" @click="loadMoreEvents">
          {{$t('events.loadMore') || 'Load More Events'}}
        </button>
      </div>
    </div>

    <div class="row" v-if="error">
      <div class="col-md-12">
        <div class="text-danger">
          {{$t('events.error')}}: {{error}}
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import $backend from '../backend'

export default {
  name: 'Events',
  metaInfo () {
    return {
      title: this.$t('events.title')
    }
  },
  data () {
    return {
      // title is not needed in data, use $t directly in metaInfo
      athlete: {},
      events: [],
      error: '',
      code: '',
      loading: false,
      offset: 0,
      limit: 20,
      hasMore: true
    }
  },
  created () {
    if (localStorage.hasOwnProperty('athlete')) {
      const athleteData = localStorage.getItem('athlete')
      if (athleteData && athleteData !== 'undefined') {
        try {
          this.athlete = JSON.parse(athleteData)
        } catch (e) {
          console.error('Error parsing athlete data:', e)
          localStorage.removeItem('athlete')
        }
      }
    }
    this.offset = 0
    this.loadEvents()
  },
  methods: {
    loadEvents: function (append = false) {
      this.loading = true
      $backend.getEvents(this.offset, this.limit).then(response => {
        const newEvents = response.data.events
        if (append) {
          this.events = this.events.concat(newEvents)
        } else {
          this.events = newEvents
        }
        // If fewer than limit returned, no more events
        this.hasMore = newEvents.length === this.limit
        this.loading = false
      }).catch(error => {
        this.error = error.data.msg
        this.loading = false
      })
    },
    loadMoreEvents: function () {
      this.offset += this.limit
      this.loadEvents(true)
    },
    view: function (slug) {
      this.$router.push({ name: 'eventDetail', params: { slug: slug } })
    },
    isUpcomingEvent: function (fromTs) {
      return fromTs > Date.now()
    },
    isExpiredEvent: function (toTs) {
      return toTs < Date.now()
    },
    getEventCssClass (event) {
      let css = 'card mb-6 box-shadow bg-light'

      if (this.isExpiredEvent(event.to * 1000)) {
        css += 'text-white bg-secondary'
        return css
      }
      if (this.isUpcomingEvent(event.from * 1000)) {
        css += 'text-white bg-info'
        return css
      }
      return css
    }
  },
  computed: {
    now: function () {
      return new Date()
    }
  },
  watch: {
    '$i18n.locale' (newLocale, oldLocale) {
      this.loadEvents()
    }
  }
}

</script>

<style lang="scss">
#events {
  background: #dee2e6;
  padding: 1rem 0;
  .event-link {
    text-decoration: none;
    color: inherit;
    display: block;
    height: 100%;
    &:hover .event-card {
      box-shadow: 0 8px 32px rgba(0,0,0,0.25);
      transform: translateY(-2px) scale(1.025);
    }
  }
  .event-card {
    border-radius: 0.75rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    transition: box-shadow 0.15s, transform 0.15s;
    background: #e9ecef;
    min-width: 0;
    .card-body {
      padding: 1rem 0.75rem 0.75rem 0.75rem;
    }
  }
  .event-img-wrapper {
    position: relative;
    .card-img-top {
      height: 180px;
      object-fit: cover;
      border-top-left-radius: 0.75rem;
      border-top-right-radius: 0.75rem;
      background: #d1d5db;
    }
    .event-badge {
      top: 0.5rem;
      left: 0.5rem;
      z-index: 2;
      font-size: 0.8em;
      padding: 0.3em 0.8em;
      border-radius: 0.7em;
      font-weight: 600;
      box-shadow: 0 1px 4px rgba(0,0,0,0.08);
      &.badge-upcoming {
        background: #17a2b8;
        color: #fff;
      }
      &.badge-expired {
        background: #dc3545;
        color: #fff;
      }
    }
  }
  .event-type-icon {
    font-size: 1.1em;
    vertical-align: middle;
    opacity: 0.8;
    color: #343a40;
  }
  .event-title {
    font-size: 1.05em;
    font-weight: 600;
    letter-spacing: 0.01em;
    vertical-align: middle;
    color: #343a40;
  }
  .event-date {
    font-size: 0.92em;
    margin-top: 0.1em;
    color: #495057;
  }
  .row {
    margin-left: 0;
    margin-right: 0;
  }
  .col-12, .col-sm-6, .col-lg-6 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
@media (max-width: 991px) {
  #events .col-lg-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }
}
@media (max-width: 767px) {
  #events .col-sm-6, #events .col-lg-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }
  #events .event-card {
    width: 100% !important;
    padding: 0.5rem 0.3rem !important;
  }
  #events .event-img-wrapper .card-img-top {
    height: 80px !important;
  }
  #events .event-card .card-body {
    padding: 0.75rem 0.5rem 0.5rem 0.5rem !important;
  }
  #events .event-title {
    font-size: 0.95em !important;
  }
  #events .event-type-icon {
    font-size: 0.95em !important;
  }
  #events .event-date {
    font-size: 0.82em !important;
  }
}

@keyframes run-pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
}

.running-animate {
  animation: run-pulse 1s infinite;
  color: #17a2b8;
}
</style>
